// Sends recorded chunks to the server as they're made: registers each track,
// uploads its chunks in order with a checksum, and tells the server when a
// track has ended. Chunks are kept (see chunk-store) until the server has
// them; failures back off and retry, and a reloaded page resumes the queue.

import { StudioError, studioFetch } from './studio-api';
import type { ChunkStore, StoredTrack } from './chunk-store';

export type UploadState = 'idle' | 'uploading' | 'retrying' | 'offline' | 'unauthorized';

export interface UploadStats {
  state: UploadState;
  /** Chunks recorded but not yet on the server. */
  pending: number;
  /** Chunks the server has from this queue. */
  sent: number;
  /** Chunks the server refused for good (shouldn't happen). */
  dropped: number;
  /** Chunks the server never got, per finished track (lost locally). */
  missing: number;
}

export interface UploadQueueOptions {
  token: string;
  participantId: string;
  store: ChunkStore;
  /** Hex sha256 of a chunk. */
  digest?: (blob: Blob) => Promise<string>;
  request?: typeof studioFetch;
  /** Hold uploads until this local time (ms): `?simulateOffline=`. */
  pausedUntil?: number;
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (t: unknown) => void;
}

export const RETRY_MIN_MS = 1000;
export const RETRY_MAX_MS = 30_000;

export async function sha256Hex(blob: Blob): Promise<string> {
  const hash = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  return [...new Uint8Array(hash)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** A failure worth retrying (network, server trouble), versus a final no. */
export function retryable(err: unknown): boolean {
  if (!(err instanceof StudioError)) return true;
  return err.status >= 500 || err.status === 408 || err.status === 429 || err.code === 'checksum_mismatch';
}

export class UploadQueue {
  private tracks = new Map<string, StoredTrack>();
  private pending = new Map<string, Set<number>>();
  private stats: UploadStats = { state: 'idle', pending: 0, sent: 0, dropped: 0, missing: 0 };
  private listeners = new Set<(s: UploadStats) => void>();
  private running = false;
  private again = false;
  private closed = false;
  private timer: unknown = null;
  private delay = RETRY_MIN_MS;
  private readonly o: Required<Omit<UploadQueueOptions, 'pausedUntil'>> & { pausedUntil: number };

  constructor(opts: UploadQueueOptions) {
    this.o = {
      digest: sha256Hex,
      request: studioFetch,
      now: () => Date.now(),
      setTimer: (fn, ms) => setTimeout(fn, ms),
      clearTimer: (t) => clearTimeout(t as ReturnType<typeof setTimeout>),
      pausedUntil: 0,
      ...opts,
    };
  }

  get snapshot(): UploadStats {
    return { ...this.stats };
  }

  onChange(fn: (s: UploadStats) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private set(patch: Partial<UploadStats>) {
    const pending = [...this.pending.values()].reduce((n, s) => n + s.size, 0);
    this.stats = { ...this.stats, ...patch, pending };
    for (const fn of this.listeners) fn(this.snapshot);
  }

  /**
   * Picks up tracks and chunks left from an earlier visit. A track still
   * open then was cut off (reload, crash): its recorder is gone, so it ends
   * at the last chunk recorded. When it ended is unknown, so its clock drift
   * isn't corrected.
   */
  async resume(): Promise<void> {
    const { store, participantId } = this.o;
    for (const t of await store.tracks()) {
      if (t.participantId !== participantId) continue;
      if (t.finalSeq === null) {
        t.finalSeq = t.lastSeq ?? -1;
        t.endedAtServerMs = null;
        await store.putTrack(t);
      }
      this.tracks.set(t.id, t);
    }
    for (const [trackId, seq] of await store.chunkKeys()) if (this.tracks.has(trackId)) this.mark(trackId, seq);
    this.set({});
    this.kick();
  }

  async addTrack(track: StoredTrack): Promise<void> {
    this.tracks.set(track.id, track);
    await this.o.store.putTrack(track);
    this.kick();
  }

  async addChunk(trackId: string, seq: number, blob: Blob): Promise<void> {
    if (!this.tracks.has(trackId)) {
      // Its track was refused (the take is gone): nowhere to send it.
      this.stats.dropped += 1;
      this.set({});
      return;
    }
    await this.o.store.putChunk(trackId, seq, blob);
    const t = this.tracks.get(trackId)!;
    if (seq > (t.lastSeq ?? -1)) {
      t.lastSeq = seq;
      await this.o.store.putTrack(t);
    }
    this.mark(trackId, seq);
    this.set({});
    this.kick();
  }

  /** Recording of a track ended; once its chunks are sent the server is told. */
  async finish(trackId: string, finalSeq: number, endedAtServerMs: number | null): Promise<void> {
    const t = this.tracks.get(trackId);
    if (!t) return;
    Object.assign(t, { finalSeq, endedAtServerMs });
    await this.o.store.putTrack(t);
    this.kick();
  }

  /** True while anything still has to reach the server. */
  get busy(): boolean {
    return this.tracks.size > 0;
  }

  /** Retry now (e.g. back online). */
  retryNow(): void {
    this.delay = RETRY_MIN_MS;
    this.kick();
  }

  close(): void {
    this.closed = true;
    if (this.timer !== null) this.o.clearTimer(this.timer);
    this.timer = null;
    this.listeners.clear();
  }

  private mark(trackId: string, seq: number) {
    const s = this.pending.get(trackId) ?? new Set<number>();
    s.add(seq);
    this.pending.set(trackId, s);
  }

  private unmark(trackId: string, seq: number) {
    const s = this.pending.get(trackId);
    s?.delete(seq);
    if (s && s.size === 0) this.pending.delete(trackId);
  }

  private kick() {
    if (this.closed) return;
    if (this.running) {
      this.again = true;
      return;
    }
    if (this.timer !== null) {
      this.o.clearTimer(this.timer);
      this.timer = null;
    }
    void this.run();
  }

  private schedule(ms: number) {
    if (this.closed) return;
    this.timer = this.o.setTimer(() => {
      this.timer = null;
      this.kick();
    }, ms);
  }

  private async run(): Promise<void> {
    this.running = true;
    try {
      do {
        this.again = false;
        const wait = this.o.pausedUntil - this.o.now();
        if (wait > 0) {
          this.set({ state: 'offline' });
          this.schedule(wait);
          return;
        }
        if (!this.tracks.size) {
          this.set({ state: 'idle' });
          return;
        }
        this.set({ state: 'uploading' });
        try {
          await this.drain();
          this.delay = RETRY_MIN_MS;
        } catch (err) {
          if (err instanceof StudioError && err.status === 401) {
            // Kicked, or the token expired: nothing more can be sent.
            this.set({ state: 'unauthorized' });
            return;
          }
          this.set({ state: 'retrying' });
          this.schedule(this.delay);
          this.delay = Math.min(this.delay * 2, RETRY_MAX_MS);
          return;
        }
      } while (this.again && !this.closed);
      this.set({ state: this.tracks.size ? 'uploading' : 'idle' });
    } finally {
      this.running = false;
    }
  }

  /** One pass over everything; throws on a retryable failure. */
  private async drain(): Promise<void> {
    const { request, token, store } = this.o;
    for (const t of [...this.tracks.values()]) {
      if (this.closed) return;
      if (!t.registered) {
        try {
          await request(`/api/studio/sessions/${t.sessionId}/tracks`, token, {
            method: 'POST',
            body: JSON.stringify({
              id: t.id,
              kind: t.kind,
              mime: t.mime,
              startedAtServerMs: t.startedAtServerMs,
              clockOffsetMs: t.clockOffsetMs,
              clockRttMs: t.clockRttMs,
              deviceLabel: t.deviceLabel,
            }),
          });
        } catch (err) {
          if (retryable(err) || (err as StudioError).status === 401) throw err;
          // The take is gone or the id clashes: this track can't be saved.
          await this.forget(t, true);
          continue;
        }
        t.registered = true;
        await store.putTrack(t);
      }

      for (const seq of [...(this.pending.get(t.id) ?? [])].sort((a, b) => a - b)) {
        if (this.closed) return;
        const blob = await store.getChunk(t.id, seq);
        if (blob) {
          try {
            await request(`/api/studio/tracks/${t.id}/chunks/${seq}`, token, {
              method: 'PUT',
              body: blob,
              headers: { 'Content-Type': 'application/octet-stream', 'X-Chunk-Sha256': await this.o.digest(blob) },
            });
            this.stats.sent += 1;
          } catch (err) {
            if (retryable(err) || (err as StudioError).status === 401) throw err;
            this.stats.dropped += 1;
          }
          await store.deleteChunk(t.id, seq);
        }
        this.unmark(t.id, seq);
        this.set({});
      }

      if (t.finalSeq !== null && !this.pending.has(t.id)) {
        try {
          const res = await request<{ missing: number[] }>(`/api/studio/tracks/${t.id}/complete`, token, {
            method: 'POST',
            body: JSON.stringify({ finalSeq: t.finalSeq, endedAtServerMs: t.endedAtServerMs }),
          });
          this.stats.missing += res.missing.length;
        } catch (err) {
          if (retryable(err) || (err as StudioError).status === 401) throw err;
        }
        await this.forget(t, false);
      }
    }
  }

  private async forget(t: StoredTrack, dropChunks: boolean) {
    if (dropChunks) {
      for (const seq of this.pending.get(t.id) ?? []) {
        await this.o.store.deleteChunk(t.id, seq);
        this.stats.dropped += 1;
      }
      this.pending.delete(t.id);
    }
    this.tracks.delete(t.id);
    await this.o.store.deleteTrack(t.id);
    this.set({});
  }
}
